import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Alert, AlertTitle } from "@material-ui/lab";
import {
  Box,
  Container,
  Button,
  Paper,
  LinearProgress,
  Typography,
} from "@material-ui/core";
import WatchVideoButton from "./WatchVideoButton";

function LinearProgressWithLabel(props) {
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress
          variant="determinate"
          {...props}
          style={{ height: "10px", borderRadius: "2px" }}
        />
      </Box>
      <Box minWidth={35}>
        <Typography variant="body2" color="textSecondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "12px",
    borderRadius: "4px",
    marginBottom: "24px",
    marginTop: "24px",
    width: "100%",
    "& > * + *": {
      marginTop: theme.spacing(2),
    },
  },
  alert: {
    border: "0.5px solid rgba(34, 57, 69, 0.4)",
  },
  videoContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
  },
  continueButton: {
    minWidth: "128px",
  },
  contactSupportButton: {
    minWidth: "150px",
  },
}));

const body = `Hi AgencyIQ Team,

My agency setup could not finish due to a technical issue. I need assistance to complete the setup.
Please help me get this resolved.

Thank you`;
const subject = `Setup Issue - Assistance Needed`;
const encodedBody = encodeURIComponent(body);
const encodedSubject = encodeURIComponent(subject);
const email = process.env.REACT_APP_SUPPORT_EMAIL;

const logoutPath = "/api/auth/logout";

const handleLogout = () => {
  window.location.href = logoutPath + "?fromLogoutButton=true";
};

export default function WaitingArea({
  setupRequestReceived,
  expired,
  supportPending,
  progress,
}) {
  const classes = useStyles();
  const inProgress = !setupRequestReceived && !expired && !supportPending;

  const inProgressOrSuccessful = inProgress || setupRequestReceived;

  return (
    <Container maxWidth="md">
      <Paper className={classes.root} variant="outlined">
        {inProgressOrSuccessful && (
          <>
            <Typography variant="h6">
              Setting You Up, Almost There...
            </Typography>
            <Typography variant="subtitle">
              Setup in progress… Please stay on this page — you’ll be redirected
              automatically in just a few moments.
            </Typography>
            {inProgress && <LinearProgressWithLabel value={progress} />}
          </>
        )}
        {expired && !setupRequestReceived && !supportPending && (
          <Alert
            severity="warning"
            className={classes.alert}
            action={
              <Button
                href={`mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`}
                className={classes.contactSupportButton}
              >
                Contact Support
              </Button>
            }
          >
            <AlertTitle>Something Went Wrong</AlertTitle>
            We were not able to complete your setup due to a technical issue.
            Click on the button to contact our support team — they will help you
            get things back on track.
          </Alert>
        )}
        {supportPending && (
          <Alert severity="warning">
            <AlertTitle>AMS Support Coming Soon!</AlertTitle>
            Your AMS is not currently supported. We are actively working to
            expand our AMS integrations - including yours. Stay tuned, and we
            will notify you as soon as it is ready!
          </Alert>
        )}
        {inProgressOrSuccessful && (
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">
              While you wait, check out this quick video to see how AgencyIQ
              helps agencies like yours grow and drive more revenue.
            </Typography>
            <Box style={{ minWidth: "150px" }}>
              <WatchVideoButton />
            </Box>
          </Box>
        )}
        {!inProgressOrSuccessful && (
          <Box style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleLogout} variant="contained" color="primary">
              Logout
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
